// What stops a program draft from saving, and WHERE (#336, brief §9). One
// list for two readers: `buildPayload` refuses the save on its first entry,
// and the phone editor walks the whole list («3 problems ‹ ›»), each entry
// taking the author to its day, exercise and round. One function, so the
// check that blocks the save and the list the author sees cannot disagree.
//
// The predicates are the cell vocabulary's own (programEditBuild,
// programEditTimeAxis): this file only says where each one fails.
// Keep free of React and any UI import.

import type { Draft } from "./programEdit.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { amrapWithTimeError, pauseError, timeAxisError } from "./programEditTimeAxis.js";
import { cellRepsRangeInvalid } from "./programRepsRange.js";
import {
  amrapOf,
  buildIntensity,
  isCellAbsent,
  kindAt,
  parseRepsToken,
  VALUELESS_KINDS,
  type BuildReasonCode,
} from "./programEditBuild.js";

/** Exercises in one day: the schema's slots.max(20). Here, beside the
 *  check, so the actions that add to a day read the same number. */
export const MAX_DAY_EXERCISES = 20;

/** One reason the draft cannot be saved. `code` is what the UI words
 *  (program.errCode.<code>); `reason` is an English breadcrumb for logs.
 *  The position is as deep as the check knows: a day, an exercise of it
 *  (by id, so a move does not lose it), a set row, a round. None at all: the
 *  problem is about the whole program (its name). */
export type DraftProblem = {
  code: BuildReasonCode;
  reason: string;
  sIdx?: number;
  slotId?: string;
  rowIdx?: number;
  week?: number;
};

/** Every problem of the draft, in the order the save meets them: the name
 *  (the editor's own guard: buildPayload skips it, see there),
 *  the empty or over-full days, then day by day (day name, exercise, sets, cells), then
 *  supersets that mix kinds. One entry per place: two bad cells of the same
 *  exercise in the same round are one stop for the author, the first found.
 *  `cellUnit`: see buildIntensity. Empty: the draft passes these checks (the
 *  schema still has the last word, in buildPayload). */
export const locateDraftProblems = (
  draft: Draft,
  cellUnit: UnitsPreference,
): DraftProblem[] => {
  const out: DraftProblem[] = [];
  const seen = new Set<string>();
  // `slotIdx` only keys the place: an id can be "" on a slot read without one.
  const push = (p: DraftProblem, slotIdx?: number) => {
    const key = `${p.sIdx ?? "-"}|${slotIdx ?? "-"}|${p.week ?? "-"}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(p);
  };

  if (draft.name.trim().length === 0)
    push({ code: "programNameEmpty", reason: "Program name empty" });
  // Friendly words for the schema's slots.min(1): a new program, or a day
  // emptied, would otherwise fail with a raw zod path.
  draft.sessions.forEach((session, sIdx) => {
    if (session.slots.length === 0)
      push({ code: "sessionEmpty", reason: "Session has no exercises", sIdx });
    // The schema's slots.max(20), with its day, instead of a raw zod path.
    else if (session.slots.length > MAX_DAY_EXERCISES)
      push({ code: "sessionTooManyExercises", reason: "Session over the exercise cap", sIdx });
  });

  draft.sessions.forEach((session, sIdx) => {
    if (session.name.trim().length === 0)
      push({ code: "sessionNameEmpty", reason: "Session name empty", sIdx });
    session.slots.forEach((slot, slotIdx) => {
      const at = { sIdx, slotId: slot.id };
      if (slot.exerciseId.trim().length === 0)
        push({ code: "slotNoExercise", reason: "Slot has no exercise", ...at }, slotIdx);
      if (slot.setRows.length === 0)
        push({ code: "slotNoSets", reason: "Slot has no sets", ...at }, slotIdx);
      if (slot.inactiveWeeks.every(Boolean))
        push({ code: "slotAllWeeksInactive", reason: "Slot inactive on every week", ...at }, slotIdx);
      slot.setRows.forEach((row, rowIdx) => {
        if (row.cells.length !== draft.cycleWeeks) {
          push({ code: "rowWidthMismatch", reason: "Row width mismatch", ...at, rowIdx }, slotIdx);
          return;
        }
        for (let week = 0; week < row.cells.length; week++) {
          // Inactive weeks emit sets: [] - their cells are placeholders
          // and never reach the payload, so don't validate them. Nor a set
          // this round does not have (#336 S1).
          if (slot.inactiveWeeks[week] || isCellAbsent(row, week)) continue;
          const cell = row.cells[week]!;
          const here = { ...at, rowIdx, week };
          const tok = parseRepsToken(cell.reps);
          if (tok == null) {
            push({ code: "invalidReps", reason: "Invalid reps", ...here }, slotIdx);
            continue;
          }
          if (cellRepsRangeInvalid(row, cell, tok.reps)) {
            push({ code: "invalidRepsRange", reason: "Rep range not around the target", ...here }, slotIdx);
            continue;
          }
          if (timeAxisError(row.workKind, cell.workDurationSec, cell.restSec)) {
            push({ code: "invalidSeconds", reason: "Invalid work/rest seconds", ...here }, slotIdx);
            continue;
          }
          if (pauseError(cell.pausePhase, cell.pauseSec)) {
            push({ code: "invalidPause", reason: "Pause with no valid seconds", ...here }, slotIdx);
            continue;
          }
          if (amrapWithTimeError(row.workKind, amrapOf(cell))) {
            push({ code: "amrapWithTime", reason: "AMRAP floor on a clock-bounded bout", ...here }, slotIdx);
            continue;
          }
          const kind = kindAt(row, week);
          if (!VALUELESS_KINDS.has(kind)) {
            const intensity = buildIntensity(kind, cell.intensityValue, cellUnit);
            if ("error" in intensity)
              push({ code: intensity.code, reason: intensity.error, ...here }, slotIdx);
          }
        }
      });
    });
  });

  // Mixed kinds inside a single group id: the same defense-in-depth check
  // WorkoutLog runs. The editor can't normally produce a mismatch, but a
  // stored program re-loaded with legacy or hand-edited data could.
  draft.sessions.forEach((session, sIdx) => {
    const seenKind = new Map<string, string>();
    session.slots.forEach((slot, slotIdx) => {
      if (!slot.group) return;
      const prior = seenKind.get(slot.group.id);
      if (prior && prior !== slot.group.kind)
        push(
          {
            code: "groupKindMixed",
            reason: `mixed kinds for group ${slot.group.id}`,
            sIdx,
            slotId: slot.id,
          },
          slotIdx,
        );
      seenKind.set(slot.group.id, slot.group.kind);
    });
  });
  return out;
};
